/* global client */
import layerVector from '../node_modules/ol/layer/Vector.js';
import Draw from '../node_modules/ol/interaction/Draw.js';
import {fromCircle} from '../node_modules/ol/geom/Polygon.js';
import {unByKey} from '../node_modules/ol/Observable.js';

import LayerStyle from './LayerStyle.js';
import Tooltip from './Tooltip.js';
import ModalAttributeDialog from './ModalAttributeDialog.js';
import makeStyle from './makeStyle.js'
import Snap from './Snap.js'

// test for swco
import postFeatureToServer from './postFeatureToServer.js'; // THIS IMPORT IS USED! - despite what vs code thinks
import {trav} from './trav.js'

/**
 * @constructor adds 'lyrDraw' to map with style rwg.config.lyrDrawStyles
 * @param {ol.map} map
 * @return {function} - switchboard
 *
 * Provides 'addInteraction()' function with drawing start & end handlers
 * ALL drawing occurs on lyrDraw with source lyrDraw. This includes linear and
 * area measuring objects, temporary drawing objects and drawn objects that will
 * be saved to the server. This is confusing. On drawend features should be moved
 * from lyrDraw to a target layer & source if they are anymore than tmp features.
 *
 * If rwg.config.fullDrawing==true the drawing tools will be available which allow styling & labeling.
 * If rwg.config.saveFeatures==true then the saveFeatures() function is called. Note that fullDrawing must be
 * true and the app must be in "desktop" (not "mobile") view mode for the saveFeatures() function to get called.
 * rwg.config.editable defines the editable layer and also plays in somewhere
 * The newly created feature is on 'lyrDraw'. If there is an editable layer the new feature is
 * not automatically put on it. In Sweetewater the new features is saved to the server but remains on lyrDraw
 * until the next time the app is loaded. In Sublette the the server returns the new features and crudCallback()
 * adds it to the editable layer and removes if from lyrDraw. This allows the server to add a pk and any other
 * server-side processing or attributes.
 */

export default function(map) {
  let draw;
  let snap = new Snap(map);
  let layerStyle = new LayerStyle('lyrDraw', rwg.config.lyrDrawStyles);
  let source = rwg.sources.get('lyrDraw');  // 'lyrDraw' default to a simple, empty ol Source but can be overwritten by config to be an ajax geoJSON source
  let drawingStyle = makeStyle({  // style that's used as feature is being drawn
    stroke: {
      color: 'rgba(0, 0, 0, 0.5)',
      lineDash: [10, 10],
      width: 2
    },
    circle: {
      radius: 5,
      stroke: {
        color: 'rgba(0, 0, 0, 0.7)'
      },
      fill: {
        color: 'rgba(255, 255, 255, 0.2)'
      }
    }
  });
  let display = true;
  if (typeof rwg.layers.getLayerByName('wfs') !== 'undefined') {
    display = rwg.layers.getLayerByName('wfs').state;
  }

  map.addLayer(new layerVector({
    source: source,
    title: 'lyrDraw',
    style: layerStyle.styleFunc,
    visible: display,
    zIndex: 9,
  }));

  /**
   * @param {ol.geomType} type  Polygon | LineString | Point | Circle
   * @param {string} mode  'trav' | undefined
   */
  function addInteraction(type, mode) {
    let sketch;
    let listener;
    let tooltip
    draw = new Draw({
      source: source,
      type: type,
      style: drawingStyle
    });

    map.addInteraction(draw);
    snap('on')

    /*
    // The snap interaction must be added after the Modify and Draw interactions
    // in order for its map browser event handlers to be fired first. Its handlers
    // are responsible of doing the snapping.
    // I have a snap example on x1 in htdocs/ol-snap/
    snap = new Snap({
      source: map.getLayerByTitle('lyrDraw').getSource(),
    });
    map.addInteraction(snap)
    */

    draw.on('drawstart', (evt) => {
      sketch = evt.feature;
      let sketchGeom = sketch.getGeometry();
      tooltip = new Tooltip(map);
      listener = sketchGeom.on('change', (evt) => {
        tooltip.update(evt.target, 'create');
      })
      sketch.set('tooltipOverlay_', tooltip); // so that it can be modified when editing the feature too
    })

    draw.on('drawend', (evt) => {
      let feature
      if (mode === 'trav') {
        feature = trav({
          startPoint: evt.feature.getGeometry().getCoordinates(),
          source: source,
        })
      } else {
        feature = evt.feature
      }
      if (!feature) return
      let geom = feature.getGeometry()
      let geomType = geom.getType()

      if (geomType === 'Polygon') {
        // feature.setGeometry(geom.simplify(rwg.map.getView().getResolution() / 2))
      } else if (geomType === "LineString") {
        // feature.setGeometry(geom.simplify(rwg.map.getView().getResolution() * 0.5))
      } else if (geomType === 'Circle') { // GeoJSON does not support circles so covert circle to a poly
        feature.setGeometry(fromCircle(feature.getGeometry()))
      }

      if (rwg.config.fullDrawing && rwg.layout.get() === 'tabbed') { // checking for tabbed layout
        let lyrStyle = map.getLayerByTitle('lyrDraw').getStyle()(feature, map.getView().getResolution());
        feature.setStyle([lyrStyle[0].clone()]);

        feature.set('revision0_', feature.getRevision() + 1);
        rwg.selection.selectInteraction.getFeatures().push(feature);  // this doesn't seem necessary

        /*grep -r --include *.js --include *.php saveFeatures
          sweetwater/htdocs/mapserver/etc/supplemental.php:  rwg.config.saveFeatures = state
          sweetwater/htdocs/mapserver/etc/supplemental.php:  rwg.config.saveFeatures = 'with-dialog';
          sweetwater/htdocs/mapserver/etc/supplemental.php:  rwg.config.saveFeatures = false;
          sweetwater/htdocs/mapserver/etc/supplemental.php:  rwg.config.saveFeatures = 'with-dialog';

          sublette/htdocs/mapserver/etc/conf.js:             rwg.config.saveFeatures = state

          tcwp/htdocs/etc/conf.js:                           rwg.config.saveFeatures = state

          cpc/htdocs/mapserver/etc/conf.js:                  saveFeatures: 'with-dialog', // true
          cpc/htdocs/mapserver/etc/conf-jq-ui-tabs.js:       saveFeatures: 'with-dialog', // true
        */
        if (rwg.config.saveFeatures) {
          feature.set('client', client || 'none');  // gotta have at least one attribute on new object or insert fails in crud.php
          draw.setActive(false);  // prevent creation of a new object until attributes have been created for this one
          new ModalAttributeDialog(feature, function() {
            postFeatureToServer((feature.get('ogc_fid')) ? 'update' : 'new', feature);
            draw.setActive(true);
          });
        } else if (geomType === 'Point') {  // open a dialog to enter text
          draw.setActive(false);  // prevent creation of a new object until attributes have been created for this one
          new ModalAttributeDialog(feature, () => draw.setActive(true));
        }
      }

      sketch = null;  // unset sketch
      unByKey(listener);
    })
  }

  // switchboard
  return function(type) {
    if (type !== 'clear' && draw instanceof Draw) {
      map.removeInteraction(snap);
      map.removeInteraction(draw);  // this disables measure tool (length or area) if one is active
      snap('off')
    }
    switch(type) {
      case 'off':   // not much to do here since we've already turned meas off w/ "map.removeInteraction(draw)" above
        break;
      case 'clear': // clear layer & tooltips
        source.clear();
        var overlays = map.getOverlays().getArray().slice(0);  // make a copy of the array
        overlays.forEach(function(ovrl) {
          if ( ovrl.getElement().className === 'tooltip tooltip-measure' ||
               ovrl.getElement().className === 'tooltip tooltip-static') {
            map.removeOverlay(ovrl);
          }
        });
        break;
      case 'area':
        addInteraction('Polygon');
        break;
      case 'dist':
        addInteraction('LineString');
        break;
      case 'point':
        addInteraction('Point');
        break;
      case 'trav':
        addInteraction('Point', type);
        break;
      case 'circle':
        addInteraction('Circle');
        break;
    }
  };

}
